import AppSettings from "../../../../Settings/AppSettings";
import React, { useContext, useEffect, useState } from "react";
import { CategoryContext } from "../CategoryContext";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ABS_ATTRIBUTES, ABS_NOT_FOUND, CF_ATTRIBUTES, CF_NOT_FOUND, LOADING_ABS, LOADING_CF, SAVING_ABS, SAVING_CF } from "../CategoryConstants";
import LoadingSuccessErrorAlert from "./LoadingSuccessErrorAlert";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Button from "Components/DesignSystem/Button/Button";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Drag_n_drop } from "Components/DesignSystem/Iconography/Icons";

/**
 * ABS / CF Attribute Order — on the design system since the category module's
 * Phase 5. Every service call, payload shape ({ id, <order field>, <enabled
 * field> }), the JSON-compare dirty gate, the stale-closure 5000ms timer and —
 * deliberately — the § 7 UNGATED Edit (no checkPermission, pinned as a
 * must-not-regress) are the legacy logic verbatim. Rendering changed: DS
 * Accordion (controlled, § 192 actions slot), @hello-pangea/dnd KEPT with rows
 * restyled on role tokens, and the local uncontrolled ButtonToggle replaced by
 * the CONTROLLED DS Toggle driven by `item.abs_enabled` / `item.cf_enabled` —
 * which is spec-visible: Reset now visually resets the switches, where the
 * legacy toggles kept their own stale internal state.
 *
 * Two instances can mount on one page (Abs + CF), so every testid is templated
 * from the `id` prop; `id='Abs'` produces exactly the legacy testids.
 */
const AbsAttributeOrder = ({title, id}) => {
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const { editingItem } = useContext(CategoryContext);
  const [loading, setLoading] = useState({ status: "", message: "", });
  const [list, setList] = useState(null);
  const [editPage, setEditPage] = useState(false);
  const [collapse, setCollapse] = useState(false);
  const [absAttributeOrder, setAbsAttributeOrder] = useState(null);
  const idKey = `${id}`.toLowerCase();

  useEffect(() => {
    setAbsAttributeOrder(null);
    setLoading({ status: "", message: "", });
  }, [editingItem.id]);

  useEffect(() => {
    if (list === null && collapse) {
      load_associated_Abs_attributes();
    }
  }, [collapse]);

  useEffect(() => {
    if (editPage) {
      load_associated_Abs_attributes();
    }
  }, [editPage]);

  async function load_associated_Abs_attributes() {
    setLoading({ status: "Loading", message: title === CF_ATTRIBUTES ? LOADING_CF : LOADING_ABS });
    let res = await ServiceWrapper.doGet(
      `${api_base_url}category/${editingItem.id}/${title === CF_ATTRIBUTES ? 'cf_attributes/list' : 'absattributes/list'}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      setLoading({ status: "Success", message: undefined });
      setAbsAttributeOrder(res.data.data);
      setList(res.data.data);
    } else {
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }


  const handleToggle = (id) => {
    const fieldName = title === ABS_ATTRIBUTES ? "abs_enabled" : "cf_enabled";
    setList((prevList) =>
      prevList.map((item) =>
        item.id === id ? { ...item, [fieldName]: title === CF_ATTRIBUTES ? !item.cf_enabled :!item.abs_enabled } : item
      )
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const newList = Array.from(list);
    const [movedItem] = newList.splice(result.source.index, 1);
    newList.splice(result.destination.index, 0, movedItem);
    setList(newList);
  };

  async function handle_save() {
    try {
      setLoading({
        status: "Loading",
        message: title === CF_ATTRIBUTES ? SAVING_CF : SAVING_ABS,
      });
      setAbsAttributeOrder(null);
      const newOrder = [];
      const fieldName = title === ABS_ATTRIBUTES ? "abs_enabled" : "cf_enabled";
      const orderField = title === ABS_ATTRIBUTES ? 'abs_attribute_order': 'cf_attribute_order';
      list.map((item, index) => { return newOrder.push({ id: item.id, [orderField]: `${index + 1}`, [fieldName]: item?.[fieldName]}); });
       const res = await ServiceWrapper.doPost(
        `${api_base_url}category/${editingItem.id}/${title === CF_ATTRIBUTES ? 'cf_attributes/edit' : 'absattributes/edit'}`,
        newOrder,
        null,
        AuthManager.getGmsToken()
      );
      if (!res.errorfound) {
        if (res.data.done) {
          setLoading({
            status: "Success",
            message: res.data.message,
          });
          setEditPage(false);
          load_associated_Abs_attributes();
        } else {
          setLoading({
            status: "Error",
            message: res.data.message,
          });
        }
      } else {
        setLoading({
          status: "Error",
          message: "Error while saving, Please try after some time.",
        });
      }

    } catch (error) {
      console.error("handle_save ~ error:", error)
    } finally {
      setTimeout(() => {
        if (loading.status !== "Success")
          setLoading({ status: "", message: "",});
      }, 5000);
    }
  }

  function handle_cancel() {
    setEditPage(false);
    setList(absAttributeOrder);
  }

  /* Header-row actions (§ 192). ⚠ Edit stays UNGATED — no checkPermission —
     the § 7 must-not-regress bug, pinned by
     integration/AbsAttributeOrder.noPermissionCheck.test.tsx. */
  const headerActions =
    collapse && loading.status === "Success" ? (
      !editPage ? (
        absAttributeOrder && absAttributeOrder.length > 0 ? (
          <Button
            variant="subtle"
            size="sm"
            data-testid={`category-${idKey}-attribute-order-edit-btn`}
            onClick={() => {setEditPage(true);}}
          >
            Edit
          </Button>
        ) : undefined
      ) : (
        <>
          <Button
            variant="subtle"
            size="sm"
            data-testid={`category-${idKey}-attribute-order-reset-btn`}
            onClick={() => setList(absAttributeOrder)}
            disabled={ JSON.stringify(absAttributeOrder) === JSON.stringify(list)}
          >
            Reset
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid={`category-${idKey}-attribute-order-btn`}
            onClick={() => {
              handle_cancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid={`components-${idKey}attributeorder-button-1`}
            disabled={JSON.stringify(absAttributeOrder) === JSON.stringify(list)}
            onClick={() => handle_save()}
          >
            Save
          </Button>
        </>
      )
    ) : undefined;

  return (
    <Accordion
      title={`${editPage ? "Update " : ""}${title}`}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      actions={headerActions}
      data-testid={`components-${idKey}attributeorder-div-1`}
      variant="boxed"
    >
      <div className="tw-pb-4">
        <LoadingSuccessErrorAlert
          loading={loading}
          data-testid={`category-${idKey}-attribute-order-status`}
        />

        {loading.status === "Success" && (
          <>
            {editPage ? (
              <div className="tw-pt-3 tw-overflow-auto">
                <Typography
                  variant="sub-title-label"
                  as="h3"
                  className="tw-mb-2 tw-text-ink-secondary"
                >
                  Order Ranking
                </Typography>
                <table className="tw-w-full">
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                      {(provided) => (
                        <tbody {...provided.droppableProps} ref={provided.innerRef}>
                          {list.map((item, index) => (
                            <Draggable key={item.id} draggableId={item.id} index={index}>
                              {(provided) => (
                                <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="tw-bg-surface">
                                  <td className="tw-py-1">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-ctl tw-bg-surface-sunken tw-pe-5">
                                      <span className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-text-body-primary tw-text-ink tw-cursor-grab">
                                        <Drag_n_drop
                                          aria-hidden="true"
                                          className="tw-shrink-0 tw-text-ink-tertiary"
                                        />
                                        {index + 1}. {item.name}
                                      </span>
                                      <Toggle
                                        label=""
                                        aria-label={`Toggle ${item.name}`}
                                        data-testid={`${idKey}-attribute-toggle-${item.id}`}
                                        checked={(title === CF_ATTRIBUTES ? item?.cf_enabled : item.abs_enabled) ?? false}
                                        onChange={() => handleToggle(item.id)}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </table>
              </div>
            ) : (
              <div className="tw-pt-3">
                {absAttributeOrder && absAttributeOrder.length > 0 ? (
                  <div className="tw-space-y-1">
                    {absAttributeOrder.map((item, index) => {
                      const absName = `${index + 1}  ${item.name}`;
                      return (
                        <div key={absName} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-ctl tw-bg-surface-sunken tw-px-3 tw-py-1.5">
                          <Typography variant="body-primary" className="tw-text-ink">
                            {absName}
                          </Typography>
                          <Toggle
                            label=""
                            aria-label={`Toggle ${item.name}`}
                            data-testid={`${idKey}-attribute-toggle-${item.id}`}
                            disabled={true}
                            checked={(title === CF_ATTRIBUTES ? item?.cf_enabled : item.abs_enabled) ?? false}
                            onChange={() => handleToggle(item.id)}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState size="sm" title={title === CF_ATTRIBUTES ? CF_NOT_FOUND : ABS_NOT_FOUND} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Accordion>
  );
};
export default AbsAttributeOrder;
